import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// 1. Ant Design
import { Button, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
// 2. External libraries
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useNavigate } from "react-router";
// 3. Internal components
import { companyTabPath } from "@/components/Company/companyTabs";
import { DataTable } from "@/components/DataTable";
import { Filters } from "@/components/filters";
// 4. Stores
import { getRootStore } from "@/stores/RootStore";
// 5. Types, utils, styles
import { canManageCompanies } from "@/utils/permissions";
import { CORP_COMPANY_STATUS_COLORS, CREDIT_STATE_LABELS } from "@/types/company/company.common";
import { CORP_COMPANY_STATUS_LABELS } from "@/types/corp/corpSession.common";
import styles from "@/styles/CompaniesPage.module.less";
const { Text } = Typography;
/**
 * UC1111: список корпоративных клиентов (P-1). Колонки — ровно то, что отдаёт `v_corp_companies`;
 * финансовых колонок нет ни у одной роли (D14; колонки ADMINISTRATOR из P-1 отложены — FR-006 G2).
 */
const CompaniesPage = observer(() => {
    const { authStore, companiesStore } = getRootStore();
    const { dataTable, filtersStore } = companiesStore;
    const navigate = useNavigate();
    const canCreate = canManageCompanies(authStore.userRole);
    useEffect(() => {
        dataTable.setFilters(filtersStore.values);
        dataTable.loadData();
    }, [filtersStore.values]);
    const createButton = canCreate ? (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => navigate("/companies/new"), "data-testid": "corp-companies-create", children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u044E" })) : null;
    const columns = [
        {
            title: "Название",
            key: "name",
            dataIndex: "name",
            sorter: true,
            render: (_, record) => (_jsx(Button, { type: "link", className: styles.nameLink, onClick: () => navigate(companyTabPath(record.id)), children: record.name })),
        },
        {
            title: "ИНН",
            key: "inn",
            dataIndex: "inn",
            sorter: true,
            render: (_, record) => record.inn ?? "—",
        },
        {
            title: "Статус",
            key: "status",
            dataIndex: "status",
            sorter: true,
            render: (_, record) => (_jsx(Tag, { color: CORP_COMPANY_STATUS_COLORS[record.status], children: CORP_COMPANY_STATUS_LABELS[record.status] })),
        },
        {
            title: "Активные привязки",
            key: "activeLinksCount",
            dataIndex: "activeLinksCount",
            sorter: true,
            width: 170,
        },
        {
            title: "Лимит",
            key: "creditState",
            render: (_, record) => record.creditState === "LIMIT_EXHAUSTED" ? (_jsx(Tag, { color: "red", children: CREDIT_STATE_LABELS.LIMIT_EXHAUSTED })) : (_jsx(Text, { type: "secondary", children: "\u2014" })),
        },
    ];
    const dataTableConfig = {
        header: {
            title: "Корпоративные клиенты",
            actions: createButton ?? undefined,
            extraContent: (_jsx(Filters, { filtersStore: filtersStore, title: "\u041F\u043E\u0438\u0441\u043A \u0438 \u0444\u0438\u043B\u044C\u0442\u0440\u044B", showClearAll: true, showApplyButton: false })),
        },
        columns,
        rowKey: "id",
        showRefreshButton: true,
        locale: {
            emptyText: (_jsxs("div", { className: styles.empty, children: [_jsx(Text, { type: "secondary", children: "\u041A\u043E\u0440\u043F\u043E\u0440\u0430\u0442\u0438\u0432\u043D\u044B\u0445 \u043A\u043B\u0438\u0435\u043D\u0442\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" }), createButton] })),
        },
    };
    return (_jsx("div", { "data-testid": "corp-companies-table", children: _jsx(DataTable, { store: dataTable, config: dataTableConfig }) }));
});
CompaniesPage.displayName = "CompaniesPage";
export default CompaniesPage;
